import { NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { FuseNavigationService } from '@fuse/components/navigation';
import { AvailableLangs, TranslocoService } from '@ngneat/transloco';
import { LanguagesUtils } from 'app/utils/languages.utils';
import { MenuAnalyticsService } from 'app/services/menu-analytics.service';

@Component({
    selector: 'languages',
    templateUrl: './languages.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'languages',
    standalone: true,
    imports: [MatButtonModule, MatMenuModule, NgTemplateOutlet],
})
export class LanguagesComponent implements OnInit, OnDestroy {
    availableLangs: AvailableLangs;
    activeLang: string;
    flagCodes: any;

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _fuseNavigationService: FuseNavigationService,
        private _translocoService: TranslocoService,
        private _menuAnalytics: MenuAnalyticsService
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Get the available languages from transloco
        this.availableLangs = this._translocoService.getAvailableLangs();

        // Subscribe to language changes
        this._translocoService.langChanges$.subscribe((activeLang) => {
            // Get the active lang
            this.activeLang = activeLang;

        });

        // Generate flag codes based on available languages from transloco configuration
        this.flagCodes = this._generateFlagCodes();
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {}

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Set the active lang
     *
     * @param lang
     */
    setActiveLang(lang: string): void {
        // Recorded BEFORE the switch: a session opened by this gesture must start in the previous
        // language, while the new one becomes the language the visit is counted in.
        this._menuAnalytics.trackLanguageChange(this.activeLang, lang);

        // Set the active lang
        LanguagesUtils.saveLangInLocal(lang);
        this._translocoService.setActiveLang(lang);
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Generate flag codes based on available languages from transloco configuration
     * Maps language codes to their corresponding flag country codes
     * 
     * @private
     */
    private _generateFlagCodes(): { [key: string]: string } {
        const flagMapping: { [key: string]: string } = {};
        
        // Get available languages from transloco and map them to flag codes
        const availableLangs = this._translocoService.getAvailableLangs();
        
        if (Array.isArray(availableLangs)) {
            availableLangs.forEach((lang: any) => {
                const langId = typeof lang === 'string' ? lang : lang.id;
                
                // Map language codes to flag country codes
                switch (langId) {
                    case 'en':
                        flagMapping[langId] = 'uk';
                        break;
                    case 'fr':
                        flagMapping[langId] = 'fr';
                        break;
                    case 'it':
                        flagMapping[langId] = 'it';
                        break;
                    case 'pt':
                        flagMapping[langId] = 'pt';
                        break;
                    case 'es':
                        flagMapping[langId] = 'es';
                        break;
                    case 'de':
                        flagMapping[langId] = 'de';
                        break;
                    case 'tr':
                        flagMapping[langId] = 'tr';
                        break;
                    case 'ar':
                        flagMapping[langId] = 'sa'; // Using Saudi Arabia flag for Arabic
                        break;
                    case 'ja':
                        flagMapping[langId] = 'jp'; // Japanese -> Japan flag
                        break;
                    case 'ru':
                        flagMapping[langId] = 'ru';
                        break;
                    case 'zh':
                        flagMapping[langId] = 'cn'; // Chinese -> China flag
                        break;
                    case 'el':
                        flagMapping[langId] = 'gr'; // Greek -> Greece flag
                        break;
                    case 'th':
                        flagMapping[langId] = 'th'; // Thai -> Thailand flag
                        break;
                    case 'hi':
                        flagMapping[langId] = 'in'; // Hindi -> India flag
                        break;
                    case 'ro':
                        flagMapping[langId] = 'ro'; // Romanian -> Romania flag
                        break;
                    case 'he':
                        flagMapping[langId] = 'il'; // Hebrew -> Israel flag
                        break;
                    default:
                        // If no specific flag mapping is found, use the language code as country code
                        flagMapping[langId] = langId;
                        break;
                }
            });
        }
        
        return flagMapping;
    }
}
